import { Fragment, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { formatDuration } from './syncTime';
import '../../styles/components/SyncHistory.scss';

const API_URL = process.env.REACT_APP_API_URL;
const PAGE_SIZE = 20;

const PROVIDERS = ['cdo', 'promoopcion', 'marpico', 'shopify', 'catalogos'];

const STATUS_LABELS = {
  running: 'En curso',
  finished: 'Completada',
  failed: 'Falló',
  interrupted: 'Interrumpida',
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admToken')}` });

const dateFormatter = new Intl.DateTimeFormat('es-CO', {
  timeZone: 'America/Bogota',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Historial de sincronizaciones por fecha: resultado de cada ejecución y, al
 * pulsar sus "Nuevos", los productos que creó.
 * @param {string} refreshKey - cambia cuando termina una sync, para recargar la lista.
 */
const SyncHistory = ({ refreshKey }) => {
  const [filters, setFilters] = useState({ provider: '', from: '', to: '' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Productos nuevos de la fila abierta.
  const [openId, setOpenId] = useState(null);
  const [created, setCreated] = useState(null);
  const [createdError, setCreatedError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: PAGE_SIZE };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data: body } = await axios.get(`${API_URL}/admin/sync/runs`, { headers: authHeaders(), params });
      setData(body);
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo leer el historial de sincronizaciones.');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const setFilter = (key) => (e) => {
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));
    setPage(1);
    setOpenId(null);
  };

  const clearFilters = () => {
    setFilters({ provider: '', from: '', to: '' });
    setPage(1);
    setOpenId(null);
  };

  const toggleCreated = async (runId) => {
    if (openId === runId) { setOpenId(null); return; }
    setOpenId(runId);
    setCreated(null);
    setCreatedError('');
    try {
      const { data: body } = await axios.get(`${API_URL}/admin/sync/runs/${runId}/created`, { headers: authHeaders() });
      setCreated(body.products);
    } catch (err) {
      setCreatedError(err.response?.data?.message || 'No se pudieron leer los productos nuevos.');
    }
  };

  const runs = data?.runs || [];
  const hasFilters = filters.provider || filters.from || filters.to;

  return (
    <div className="sync-history">
      <div className="sync-history__head">
        <h3>Historial de sincronización</h3>
        {data && <span className="sync-history__total">{data.total} sincronizaciones</span>}
      </div>

      <div className="sync-history__filters">
        <label>
          <span>Bodega</span>
          <select value={filters.provider} onChange={setFilter('provider')}>
            <option value="">Todas</option>
            {PROVIDERS.map((p) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
          </select>
        </label>
        <label>
          <span>Desde</span>
          <input type="date" value={filters.from} max={filters.to || undefined} onChange={setFilter('from')} />
        </label>
        <label>
          <span>Hasta</span>
          <input type="date" value={filters.to} min={filters.from || undefined} onChange={setFilter('to')} />
        </label>
        {hasFilters && (
          <button type="button" className="sync-history__clear" onClick={clearFilters}>Limpiar</button>
        )}
      </div>

      {error && <p className="sync-history__message sync-history__message--error">{error}</p>}
      {!error && loading && !data && <p className="sync-history__message">Cargando…</p>}
      {!error && data && runs.length === 0 && (
        <p className="sync-history__message">No hay sincronizaciones en ese rango.</p>
      )}

      {runs.length > 0 && (
        <div className={`sync-history__table-wrap${loading ? ' is-loading' : ''}`}>
          <table className="sync-history__table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Bodega</th>
                <th>Resultado</th>
                <th>Duración</th>
                <th className="num">Nuevos</th>
                <th className="num">Actualizados</th>
                <th className="num">Omitidos</th>
                <th className="num">Errores</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const counts = run.counts || run.summary || {};
                const isOpen = openId === run.id;
                return (
                  <Fragment key={run.id}>
                    <tr className={isOpen ? 'is-open' : undefined}>
                      <td className="date">{dateFormatter.format(new Date(run.startedAt))}</td>
                      <td className="provider">{run.provider.toUpperCase()}</td>
                      <td>
                        <span className={`sync-history__status sync-history__status--${run.status}`}>
                          {STATUS_LABELS[run.status] || run.status}
                        </span>
                      </td>
                      <td className="duration">{run.durationMs != null ? formatDuration(run.durationMs) : '—'}</td>
                      <td className="num">
                        {counts.created > 0 ? (
                          <button
                            type="button"
                            className="sync-history__created-btn"
                            onClick={() => toggleCreated(run.id)}
                            aria-expanded={isOpen}
                          >
                            {counts.created} · {isOpen ? 'ocultar' : 'ver'}
                          </button>
                        ) : 0}
                      </td>
                      <td className="num">{counts.updated || 0}</td>
                      <td className="num">{counts.skipped || 0}</td>
                      <td className={`num${counts.errors ? ' errors' : ''}`}>{counts.errors || 0}</td>
                    </tr>
                    {isOpen && (
                      <tr className="sync-history__detail">
                        <td colSpan={8}>
                          {createdError && <p>{createdError}</p>}
                          {!createdError && !created && <p>Cargando…</p>}
                          {created && created.length === 0 && <p>No se encontraron productos nuevos.</p>}
                          {created && created.length > 0 && (
                            <ul>
                              {created.map((p) => (
                                <li key={p.id}>
                                  <code>{p.reference}</code>
                                  {p.slug
                                    ? <a href={`/productos/${p.slug}`} target="_blank" rel="noopener noreferrer">{p.title}</a>
                                    : <span>{p.title}</span>}
                                  {!p.image_count && <span className="sync-history__no-image">sin imagen</span>}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data?.pages > 1 && (
        <div className="sync-history__pager">
          <button type="button" disabled={page <= 1 || loading} onClick={() => { setPage(page - 1); setOpenId(null); }}>
            ← Anterior
          </button>
          <span>Página {data.page} de {data.pages}</span>
          <button type="button" disabled={page >= data.pages || loading} onClick={() => { setPage(page + 1); setOpenId(null); }}>
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default SyncHistory;
